<template>
  <div ref="thanksPage">
    <div v-if="showMoveModal" ref="activeModal">
      <booking-move-modal
        ref="bookingMoveModal"
        :item="activeItem"
        :item-ind="activeItemInd"
        :booking="booking"
        :booking-has-past-items="bookingHasPastItems"
        :quote="moveQuote"
        :quoting="moveQuoting"
        :saving="moveSaving"
        :error="moveError"
        @close="closeModal"
        @quote="requestMoveQuote"
        @confirm="submitMove"
        @reset-quote="resetMoveQuote"
      />
    </div>
    <div v-else-if="showCancelModal" ref="activeModal">
      <booking-cancel-modal
        :booking="booking"
        :item="activeItem"
        :item-ind="activeItemInd"
        :mode="cancelMode"
        :saving="cancelSaving"
        :error="cancelError"
        @close="closeModal"
        @confirm="submitCancel"
      />
    </div>
    <div v-else class="book flex flex-wrap gap-8 lg:flex-nowrap">
      <div class="flex w-full flex-grow flex-row gap-4 px-4 md:pr-4 lg:w-3/5 lg:px-0 lg:pb-20">
        <div>
          <div class="mb-4">
            <img
              v-if="showClosureEventNotice"
              class="h-auto w-[205px] max-w-full"
              :src="`${$settings.theme_url}/assets/images/illustrations/caves.svg`"
              alt="Caves illustration"
            >
            <img
              v-else
              :src="`${$settings.theme_url}/assets/images/thanks-whale.png`"
              alt="Thanks whale"
            >
          </div>
          <h1 class="mb-4 text-3xl font-bold" data-testid="booking-thanks-title">
            <template v-if="showClosureEventNotice">
              Please amend your booking to another day or
              <a href="/visit-experiences/" class="underline hover:text-primary">experience</a>.
            </template>
            <template v-else>{{ pageTitle }}</template>
          </h1>

          <h3 v-if="!showClosureEventNotice" class="mb-12">
            {{ pageIntro }}
            <span v-if="booking.orderNumber">Your order number is <span v-text="booking.orderNumber"></span></span>
          </h3>

          <div v-if="actionSuccess" class="mb-6">
            <div class="rounded-2xl bg-light px-6 py-4 text-dark">
              {{ actionSuccess }}
            </div>
          </div>

          <div v-if="showClosureEventNotice" class="prose">
            <p>
              If amending your booking doesn’t suit your schedule, please cancel. You will receive a full refund for any booking cancellation.
            </p>
            <p>
              If you have any questions about refunds or would like extra support, please call us on
              <a href="tel:+61897577411">(08) 9757 7411</a> or send us a WhatsApp message on
              <a
                target="_blank"
                rel="noopener noreferrer"
                :href="`https://api.whatsapp.com/send?phone=+61412877120&text=I would like some help with the order ${booking.orderNumber ?? ''}`"
              >+61 412 877 120</a>.
            </p>
            <p>Thanks for supporting Capes Foundation.</p>
            <p>As a not-for-profit organisation, your visit directly supports the ongoing care of the places and experiences we’re proud to share with you.</p>
          </div>
          <div v-else class="prose" v-html="textContent"></div>
        </div>
      </div>
      <div class="relative w-full px-4 py-6 lg:w-2/5 lg:px-0">
        <div v-if="!isCancelledBooking">
          <h2
            class="mb-8 text-2xl font-bold"
            :title="`Ordered at ${dateConfirmed}`"
            v-text="`Order number: ${booking.orderNumber ?? ''}`"
          ></h2>

          <order-details
            :amendment-refund="amendmentRefund"
            @move-time="openMoveModal"
            @cancel-item="openCancelItemModal"
            @cancel-booking="openCancelBookingModal"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "pinia";
import { bookingItemsAreTooClose, isEnabledFlag } from "../helpers";
import useStore from "../store";
import OrderDetails from "./../components/OrderDetails.vue";
import BookingMoveModal from "./../components/BookingMoveModal.vue";
import BookingCancelModal from "./../components/BookingCancelModal.vue";
import api from "../api";
import { attractionFromProductCode as findAttractionFromProductCode } from "../helpers/attractions";

export default {
  components: {
    OrderDetails,
    BookingMoveModal,
    BookingCancelModal,
  },
  data() {
    return {
      actionSuccess: "",
      activeItem: null,
      activeItemInd: null,
      amendmentRefund: null,
      cancelError: "",
      cancelMode: "item",
      cancelSaving: false,
      moveError: "",
      moveQuote: null,
      moveQuoting: false,
      moveSaving: false,
      moveSelection: null,
      showCancelModal: false,
      showMoveModal: false,
    };
  },
  computed: {
    ...mapState(useStore, ["options", "booking", "status", "quote", "attractions", "bookingNumber", "bookingToken"]),
    dateConfirmed() {
      return moment(this.booking.dateConfirmed).format("dddd, Do MMM YYYY - h:mma");
    },
    items() {
      return this.hasOrder
        ? this.booking.items.map((i) => {
            i.startDate = moment(i.startTime).format("dddd, Do MMM YYYY - h:mma");
            i.title =
              this.attractions.find((a) => a.rezdy_product_code === i.productCode)?.title ??
              i.productName;
            return i;
          })
        : [];
    },
    hasOrder() {
      return !!this.booking?.orderNumber;
    },
    isCancelledBooking() {
      return String(this.booking?.status ?? "").toUpperCase() === "CANCELLED";
    },
    pageTitle() {
      return this.isCancelledBooking
        ? "Your booking has been cancelled"
        : "Thank you for your booking";
    },
    pageIntro() {
      return this.isCancelledBooking
        ? "This booking is now cancelled."
        : "We can’t wait to see you!";
    },
    textContent() {
      if (this.isCancelledBooking) {
        if (this.booking?.retail_booking_status === "cancelled") {
          return `
            <p>Your cancellation has been processed for this booking.</p>
            <p>As this request has been processed outside of our <a href="/booking-policy/" class="underline" target="_blank">Terms and Conditions</a> refund period, we are unable to provide a refund on this occasion.
            If you have any questions or would like extra support, please contact our team on (08) 9757 7411 or send us a Whatsapp message on
            <a target="_blank" href="https://api.whatsapp.com/send?phone=+61412877120&text=I would like some help with the order ${this.booking.orderNumber}">+61 412 877 120</a></p>
            <p>We'd love to welcome you on a Capes Foundation experience in the future.</p>
          `;
        }

        return `
          <p>Your cancellation has been processed for this booking and you will receive a full refund.</p>
          <p>If you have any questions about refunds or would like extra support,
          please call us on <a href="tel:+61897577411">(08) 9757 7411</a> or send us a Whatsapp message on
          <a target="_blank" href="https://api.whatsapp.com/send?phone=+61412877120&text=I would like some help with the order ${this.booking.orderNumber}">+61 412 877 120</a>.</p>
          <p>We'd love to welcome you on a Capes Foundation experience in the future.</p>
        `;
      }

      return this.options.thankYouText;
    },
    bookingHasPastItems() {
      const items = this.booking?.items ?? [];

      return items.some((item) => {
        const startTime = item?.startTimeLocal ?? item?.startTime ?? null;

        return startTime ? moment(startTime).isBefore(moment()) : false;
      });
    },
    isClosureEventBooking() {
      return Boolean(
        this.booking?.is_closure_event_booking ||
        this.booking?.closure_event_booking?.active
      );
    },
    showClosureEventNotice() {
      return this.isClosureEventBooking && !this.isCancelledBooking;
    },
  },
  methods: {
    attractionFromProductCode(productCode) {
      return findAttractionFromProductCode(this.attractions, productCode);
    },
    moveBookingsEnabled(item) {
      return isEnabledFlag(this.attractionFromProductCode(item?.productCode)?.enabled_move_bookings);
    },
    cancelBookingsEnabled(item) {
      return isEnabledFlag(this.attractionFromProductCode(item?.productCode)?.enabled_cancel_bookings);
    },
    moveBookingCutoffMinutes(item) {
      return Number(this.attractionFromProductCode(item?.productCode)?.move_booking_cutoff_minutes ?? 0);
    },
    itemIsWithinMoveCutoff(item) {
      if (this.isClosureEventBooking) {
        return false;
      }

      const startTime = item?.startTimeLocal ?? item?.startTime ?? null;
      const cutoffMinutes = this.moveBookingCutoffMinutes(item);

      if (!startTime || cutoffMinutes <= 0) {
        return false;
      }

      return moment(startTime).diff(moment(), "minutes", true) <= cutoffMinutes;
    },
    movedBookingItems(nextTime, itemInd, targetAttraction = null, productCode = null) {
      return (this.booking?.items ?? []).map((currentItem, index) => ({
        ...currentItem,
        attraction:
          index === Number(itemInd)
            ? targetAttraction?.id
            : this.attractionFromProductCode(currentItem?.productCode)?.id,
        productCode:
          index === Number(itemInd)
            ? productCode ?? currentItem?.productCode
            : currentItem?.productCode,
        time:
          index === Number(itemInd)
            ? nextTime
            : currentItem?.startTimeLocal ?? currentItem?.startTime ?? null,
      }));
    },
    quantitiesToParticipants(quantities = []) {
      return quantities.reduce((participants, quantity) => {
        participants[quantity.optionLabel] = Number(quantity.value ?? 0);
        return participants;
      }, {});
    },
    bookingItemToPayload(item, overrideTime = null, index = null, cancel = false, overrides = {}) {
      const attraction = overrides.attraction ?? this.attractionFromProductCode(item.productCode);
      const productCode = overrides.productCode ?? item.productCode;

      if (!attraction) {
        return null;
      }

      return {
        id: attraction.id,
        cancel,
        index,
        original_product_code: item.productCode,
        product_code: productCode,
        time: overrideTime ?? item.startTimeLocal ?? item.startTime,
        participants: this.quantitiesToParticipants(item.quantities),
        extras: Array.isArray(item.extras)
          ? item.extras.map((extra) => ({
              id: extra.id,
              name: extra.name,
              quantity: Number(extra.quantity ?? 1),
            }))
          : [],
      };
    },
    scrollToActiveModal() {
      this.$nextTick(() => {
        this.$refs.activeModal?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    },
    scrollToThanksTop() {
      this.$nextTick(() => {
        this.$refs.thanksPage?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    },
    openMoveModal(item, itemInd) {
      this.showCancelModal = false;
      this.activeItem = item;
      this.activeItemInd = itemInd;
      this.moveError = "";
      this.moveQuote = null;
      this.moveSelection = null;
      this.actionSuccess = "";
      this.showMoveModal = true;
      this.scrollToActiveModal();
    },
    closeModal(force = false) {
      if (this.moveSaving && !force) {
        return;
      }

      if (this.cancelSaving && !force) {
        return;
      }

      this.showCancelModal = false;
      this.activeItem = null;
      this.activeItemInd = null;
      this.cancelError = "";
      this.showMoveModal = false;
      this.moveError = "";
      this.moveQuote = null;
      this.moveQuoting = false;
      this.moveSelection = null;
    },
    openCancelItemModal(item, itemInd) {
      this.showMoveModal = false;
      this.activeItem = item;
      this.activeItemInd = itemInd;
      this.cancelMode = "item";
      this.cancelError = "";
      this.actionSuccess = "";
      this.showCancelModal = true;
      this.scrollToActiveModal();
    },
    openCancelBookingModal() {
      this.showMoveModal = false;
      this.activeItem = null;
      this.activeItemInd = null;
      this.cancelMode = "booking";
      this.cancelError = "";
      this.actionSuccess = "";
      this.showCancelModal = true;
      this.scrollToActiveModal();
    },
    bookingNumberForUpdate() {
      return this.bookingNumber || this.booking.internal_id;
    },
    async updateBooking(payload) {
      const bookingNumber = this.bookingNumberForUpdate();

      if (!bookingNumber || !this.bookingToken) {
        throw new Error("We couldn’t verify your booking token. Please reload this page and try again.");
      }

      const response = await api.bookings.update(bookingNumber, this.bookingToken, payload);
      const store = useStore();
      const updatedBooking = response?.data?.booking ?? response?.data;

      if (response?.data?.requires_action) {
        return response;
      }

      if (updatedBooking?.items || updatedBooking?.orderNumber || updatedBooking?.internal_id) {
        store.setBooking(updatedBooking);
      } else {
        await store.loadBooking(bookingNumber, this.bookingToken);
      }

      return response;
    },
    movePayload({ item, time, itemInd, attraction, productCode }, payment = {}) {
      const bookingItem = this.bookingItemToPayload(
        item,
        time,
        itemInd,
        false,
        { attraction, productCode }
      );

      if (!bookingItem) {
        throw new Error("The selected booking item couldn’t be matched to an experience.");
      }

      return {
        token: this.bookingToken,
        items: [bookingItem],
        ...(Object.keys(payment).length > 0 ? { creditCard: payment } : {}),
      };
    },
    apiErrorMessage(error, fallback) {
      return error?.response?.data?.errors?.errorMessage
        || error?.response?.data?.message
        || error?.message
        || fallback;
    },
    moveDifferenceAmount(quote) {
      const difference = quote?.difference === undefined || quote?.difference === null
        ? NaN
        : Number(quote.difference);
      const differenceCents = quote?.difference_cents === undefined || quote?.difference_cents === null
        ? NaN
        : Number(quote.difference_cents);
      return Number.isFinite(difference)
        ? Math.abs(difference)
        : Number.isFinite(differenceCents)
          ? Math.abs(differenceCents) / 100
          : null;
    },
    moveConfirmationMessage(quote) {
      const amount = this.moveDifferenceAmount(quote);
      const formattedAmount = amount === null ? null : `$${amount.toFixed(2)} AUD`;

      if (quote?.payment_action === "refund") {
        return formattedAmount
          ? `Your booking has been moved. You’ll receive a refund of ${formattedAmount} to your original payment method.`
          : "Your booking has been moved. Your refund will be returned to your original payment method.";
      }

      if (quote?.payment_action === "charge") {
        return formattedAmount
          ? `Your booking has been moved. An additional payment of ${formattedAmount} has been charged.`
          : "Your booking has been moved and the additional payment has been charged.";
      }

      return "Your booking has been moved. No payment or refund was required.";
    },
    resetMoveQuote() {
      if (this.moveSaving || this.moveQuoting) {
        return;
      }

      this.moveQuote = null;
      this.moveSelection = null;
      this.moveError = "";
    },
    async requestMoveQuote(selection) {
      this.moveError = "";
      this.moveQuote = null;
      this.moveSelection = selection;
      this.moveQuoting = true;

      try {
        const bookingNumber = this.bookingNumberForUpdate();

        if (!bookingNumber || !this.bookingToken) {
          throw new Error("We couldn’t verify your booking token. Please reload this page and try again.");
        }

        if (bookingItemsAreTooClose(this.movedBookingItems(
          selection.time,
          selection.itemInd,
          selection.attraction,
          selection.productCode
        ))) {
          throw new Error("This selection is too close to another attraction in your booking. Please allow more time for travel between activities.");
        }

        const response = await api.bookings.moveQuote(
          bookingNumber,
          this.bookingToken,
          this.movePayload(selection)
        );

        this.moveQuote = response?.data ?? null;

        if (!this.moveQuote?.payment_action) {
          throw new Error("We couldn’t calculate the price for this move. Please try again.");
        }
      } catch (error) {
        this.moveQuote = null;
        this.moveError = this.apiErrorMessage(
          error,
          "We couldn’t calculate this booking move right now. Please try again."
        );
      } finally {
        this.moveQuoting = false;
      }
    },
    async cancelBooking(payload) {
      const bookingNumber = this.bookingNumberForUpdate();

      if (!bookingNumber || !this.bookingToken) {
        throw new Error("We couldn’t verify your booking token. Please reload this page and try again.");
      }

      const response = await api.bookings.cancel(bookingNumber, this.bookingToken, payload);
      const store = useStore();
      const updatedBooking = response?.data?.booking ?? response?.data;

      if (updatedBooking?.items || updatedBooking?.orderNumber || updatedBooking?.internal_id) {
        store.setBooking(updatedBooking);
      } else {
        await store.loadBooking(bookingNumber, this.bookingToken);
      }

      return response;
    },
    buildCancellationPayload(mode, item = null, itemInd = null) {
      const bookingItems = this.booking?.items ?? [];
      const targetIndexes = bookingItems
        .map((currentItem, index) => ({ currentItem, index }))
        .filter(({ currentItem }) => {
          const startTime = currentItem?.startTimeLocal ?? currentItem?.startTime ?? null;
          return startTime ? moment(startTime).isAfter(moment()) : false;
        })
        .filter(({ index }) => mode === "booking" || index === itemInd)
        .map(({ index }) => index);

      return {
        action: "cancel",
        cancel_booking: mode === "booking",
        cancel_item_indexes: targetIndexes,
        token: this.bookingToken,
        items: bookingItems
          .map((currentItem, index) =>
            this.bookingItemToPayload(
              currentItem,
              null,
              index,
              targetIndexes.includes(index)
            )
          )
          .filter(Boolean),
      };
    },
    async recaptcha() {
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          await this.$recaptchaLoaded();
          return await this.$recaptcha("login");
        } catch (error) {
          console.warn(`reCAPTCHA attempt ${attempt}/2 failed:`, error);
          if (attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            return "";
          }
        }
      }
    },
    async submitMove(selection) {
      this.moveError = "";
      this.actionSuccess = "";
      this.moveSaving = true;

      try {
        if (this.bookingHasPastItems) {
          throw new Error("This booking can’t be changed because one or more booking times are already in the past.");
        }

        const { item, time, itemInd, attraction, productCode, confirmationTokenId } = selection;

        if (!this.moveBookingsEnabled(item)) {
          throw new Error("This booking can’t be moved online.");
        }

        if (this.itemIsWithinMoveCutoff(item)) {
          throw new Error(`This booking can’t be moved within ${this.moveBookingCutoffMinutes(item)} minutes of the start time.`);
        }

        if (bookingItemsAreTooClose(this.movedBookingItems(time, itemInd, attraction, productCode))) {
          throw new Error("This selection is too close to another attraction in your booking. Please allow more time for travel between activities.");
        }

        const payment = confirmationTokenId
          ? { confirmationTokenId }
          : {};
        const payload = this.movePayload(selection, payment);

        if (!confirmationTokenId) {
          payload["g-recaptcha-response"] = await this.recaptcha();
        }

        let response = await this.updateBooking(payload);

        if (response?.data?.requires_action) {
          const paymentIntentId = await this.$refs.bookingMoveModal?.handleServerResponse(response.data);

          if (!paymentIntentId) {
            throw new Error("The additional payment wasn’t completed. Please try again.");
          }

          response = await this.updateBooking(this.movePayload(selection, { paymentIntentId }));
        }

        if (response?.data?.requires_action) {
          throw new Error("The additional payment still needs approval. Please try again.");
        }

        this.amendmentRefund = this.moveQuote?.payment_action === "refund"
          ? this.moveDifferenceAmount(this.moveQuote)
          : null;
        this.actionSuccess = this.moveConfirmationMessage(this.moveQuote);
        this.closeModal(true);
        this.scrollToThanksTop();

        if (window?.fathom) {
          window.fathom.trackEvent("booking-moved");
        }
      } catch (error) {
        this.moveError = this.apiErrorMessage(
          error,
          "We couldn’t move this booking right now. Please try again."
        );
      } finally {
        this.moveSaving = false;
      }
    },
    async submitCancel({ mode, item, itemInd }) {
      this.cancelError = "";
      this.actionSuccess = "";
      this.cancelSaving = true;

      try {
        if (mode === "booking" && (this.booking?.items ?? []).some((currentItem) => !this.cancelBookingsEnabled(currentItem))) {
          throw new Error("One or more booking items can’t be cancelled online.");
        }

        if (mode === "item" && !this.cancelBookingsEnabled(item)) {
          throw new Error("This booking item can’t be cancelled online.");
        }

        const payload = this.buildCancellationPayload(mode, item, itemInd);

        if (!payload.cancel_item_indexes.length) {
          throw new Error("There aren’t any upcoming booking items available to cancel.");
        }

        payload["g-recaptcha-response"] = await this.recaptcha();
        await this.cancelBooking(payload);

        this.actionSuccess =
          mode === "booking"
            ? "Your booking has been cancelled."
            : "Your booking item has been cancelled.";
        this.closeModal(true);
        this.scrollToThanksTop();

        if (window?.fathom) {
          window.fathom.trackEvent("booking-cancelled-" + (mode === "booking" ? "booking" : "item"));
        }
      } catch (error) {
        this.cancelError =
          error?.response?.data?.message ||
          error?.message ||
          "We couldn’t cancel this booking right now. Please try again.";
      } finally {
        this.cancelSaving = false;
      }
    },
  },
};
</script>
